import { useEffect, useMemo, useState } from 'react';
import { Alert, Button, Form, Modal } from 'react-bootstrap';
import { FormProvider, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useTranslation } from 'react-i18next';

import {
    CheckField,
    FormAlert,
    PhoneField,
    SelectField,
    SubmitButton,
    TextField,
    UserSelect,
    vEmail,
    vPhone,
    vRequired,
    type SelectOption,
    type UserPick,
} from '../form';
import { get, post, statusOf } from '../../lib/api';
import { splitPhone, combinePhone } from '../../lib/phone';
import { ModeToggle, type MappingMode } from '../mappings/ModeToggle';

interface SavedUser {
    firstName: string;
    lastName: string;
    countryCode: string;
    phoneNumber: string;
    email: string;
    userName: string;
    useNewMenu: boolean;
    office24SevenCustomerId: string | number;
}

const schema = z.object({
    customer: z.string().min(1, 'register.selectCustomerFirst'),
    firstName: vRequired(),
    lastName: vRequired(),
    phone: vPhone,
    email: vEmail,
    sendMeEmail: z.boolean(),
    useNewMenu: z.boolean(),
});
type Values = z.infer<typeof schema>;

const emptyForm: Values = {
    customer: '',
    firstName: '',
    lastName: '',
    phone: '',
    email: '',
    sendMeEmail: false,
    useNewMenu: false,
};

export function RegisterForm() {
    const { t } = useTranslation();
    const [mode, setMode] = useState<MappingMode>('add');
    const [savedUsers, setSavedUsers] = useState<SavedUser[]>([]);
    const [customerOptions, setCustomerOptions] = useState<SelectOption[]>([]);
    const [editEmail, setEditEmail] = useState('');
    const [loadedUserName, setLoadedUserName] = useState('');
    const [serverError, setServerError] = useState('');
    const [success, setSuccess] = useState('');
    const [showDelete, setShowDelete] = useState(false);
    const [deleting, setDeleting] = useState(false);

    const methods = useForm<Values>({
        resolver: zodResolver(schema),
        defaultValues: emptyForm,
        mode: 'onSubmit',
        reValidateMode: 'onChange',
    });

    function loadUsers() {
        get<SavedUser[]>('Home/GetAllUsers')
            .then((res) => setSavedUsers(res.data ?? []))
            .catch(() => setServerError(t('register.loadError')));
    }

    useEffect(() => {
        loadUsers();
        get<{ customerId24Seven: string | number; name: string }[]>('Home/GetCustomers')
            .then((res) => {
                setCustomerOptions(
                    (res.data ?? [])
                        .map((c) => ({
                            value: String(c.customerId24Seven),
                            label: `${c.customerId24Seven} - ${c.name}`,
                        }))
                        // Newest customer numbers first.
                        .sort((a, b) => Number(b.value) - Number(a.value)),
                );
            })
            .catch(() => undefined);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Clears selections and fields but leaves status messages alone, so a
    // success banner survives the post-submit reset.
    function clearForm() {
        setEditEmail('');
        setLoadedUserName('');
        methods.reset(emptyForm);
    }

    function switchMode(next: MappingMode) {
        setMode(next);
        setServerError('');
        setSuccess('');
        clearForm();
    }

    function selectEditUser(email: string) {
        setEditEmail(email);
        setSuccess('');
        const user = savedUsers.find((u) => u.email === email);
        if (!user) return;
        setLoadedUserName(user.userName);
        methods.reset({
            customer: String(user.office24SevenCustomerId ?? ''),
            firstName: user.firstName ?? '',
            lastName: user.lastName ?? '',
            phone: combinePhone(user.countryCode, user.phoneNumber),
            email: user.email ?? '',
            sendMeEmail: false,
            useNewMenu: !!user.useNewMenu,
        });
    }

    async function onSubmit(values: Values) {
        setServerError('');
        setSuccess('');
        const path = mode === 'add' ? 'activeDirectory/Register' : 'activeDirectory/Edit';
        // The backend + Authy keep country code and national number separate, so
        // split the single E.164 field back into the two fields they expect.
        const { countryCode, phoneNumber } = splitPhone(values.phone);
        try {
            const res = await post(path, {
                FirstName: values.firstName,
                LastName: values.lastName,
                CountryCode: countryCode,
                PhoneNumber: phoneNumber,
                Email: values.email,
                UserName: mode === 'add' ? values.email : loadedUserName,
                SendMeEmail: values.sendMeEmail,
                Office24SevenCustomerId: values.customer,
                UseNewMenu: values.useNewMenu,
            });
            if (res.status === 201) setSuccess(t('register.updated'));
            else if (res.status === 202) setSuccess(t('register.updatedNoEmail'));
            else setSuccess(t('register.registered'));
            loadUsers();
            clearForm();
        } catch (err) {
            const status = statusOf(err);
            if (status === 403) setServerError(t('register.emailExists'));
            else if (status === 409) setServerError(t('register.userExists'));
            else if (status === 500) setServerError(t('common.somethingWentWrong'));
            else setServerError(t('register.invalidForm'));
        }
    }

    async function confirmDelete() {
        setDeleting(true);
        try {
            await post('activeDirectory/Delete', { Email: methods.getValues('email'), SendMeEmail: false });
            setShowDelete(false);
            setSuccess(t('register.deleted'));
            loadUsers();
            clearForm();
        } catch {
            setShowDelete(false);
            setServerError(t('register.deleteError'));
        } finally {
            setDeleting(false);
        }
    }

    const emailValue = methods.watch('email');
    const userPicks = useMemo<UserPick[]>(
        () =>
            savedUsers.map((u) => ({
                value: u.email,
                name: `${u.firstName ?? ''} ${u.lastName ?? ''}`.trim(),
                email: u.email,
                phone: u.phoneNumber ?? '',
            })),
        [savedUsers],
    );

    return (
        <div className="pt-3" style={{ maxWidth: '34rem' }}>
            <p className="text-body-secondary">{t('register.description')}</p>
            <ModeToggle mode={mode} onChange={switchMode} idPrefix="register" />

            {success && (
                <Alert variant="success" dismissible onClose={() => setSuccess('')}>
                    {success}
                </Alert>
            )}
            <FormAlert onClose={() => setServerError('')}>{serverError || undefined}</FormAlert>

            {mode === 'edit' && (
                <Form.Group className="mb-3">
                    <Form.Label htmlFor="register-edit-user">{t('register.selectUser')}</Form.Label>
                    <UserSelect
                        inputId="register-edit-user"
                        users={userPicks}
                        value={editEmail}
                        onChange={selectEditUser}
                    />
                </Form.Group>
            )}

            {(mode === 'add' || editEmail) && (
                <FormProvider {...methods}>
                    <Form noValidate onSubmit={methods.handleSubmit(onSubmit)}>
                        <SelectField name="customer" label={t('register.selectCustomer')} options={customerOptions} required />
                        <TextField name="firstName" label={t('register.firstName')} required />
                        <TextField name="lastName" label={t('register.lastName')} required />
                        <PhoneField name="phone" label={t('register.phone')} required />
                        <TextField name="email" label={t('register.email')} type="email" required />
                        <CheckField name="sendMeEmail" label={t('register.sendMeEmail')} />
                        <CheckField name="useNewMenu" label={t('register.useNewMenu')} />
                        <div className="d-flex gap-2">
                            <SubmitButton loading={methods.formState.isSubmitting}>
                                {mode === 'add' ? t('register.registerUser') : t('register.saveChanges')}
                            </SubmitButton>
                            {mode === 'edit' && emailValue && (
                                <Button variant="outline-danger" type="button" onClick={() => setShowDelete(true)}>
                                    {t('register.deleteUser')}
                                </Button>
                            )}
                        </div>
                    </Form>
                </FormProvider>
            )}

            <Modal show={showDelete} onHide={() => setShowDelete(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title className="h5">{t('register.confirmDeleteTitle')}</Modal.Title>
                </Modal.Header>
                <Modal.Body>{t('register.confirmDelete')}</Modal.Body>
                <Modal.Footer>
                    <Button variant="outline-secondary" onClick={() => setShowDelete(false)}>
                        {t('common.cancel')}
                    </Button>
                    <Button variant="danger" onClick={confirmDelete} disabled={deleting}>
                        {t('register.proceed')}
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
}
